import React, { useState } from "react";
import styles from "./ChatFilterModal.module.css";
import clsx from "clsx";
import { AdminAction, EndReason } from "../../../../../../util/AdminAction";
import { enumToOptions } from "../../../../../../util/ExtensionFunction";
import { useAppDispatch } from "../../../../../../store/common/hooks";
import { fetchChatHistory } from "../../../../../../store/chat.slice";

interface ChatFilterModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const ChatFilterModal: React.FC<ChatFilterModalProps> = ({
  isOpen = false,
  onClose,
}) => {
  const endReasonOptions = enumToOptions(EndReason);
  const adminActionOptions = enumToOptions(AdminAction);

  const [memberId, setMemberId] = useState<string | undefined>();
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [endReason, setEndReason] = useState<EndReason | undefined>();
  const [adminAction, setAdminAction] = useState<AdminAction | undefined>();
  const [createdAt, setCreatedAt] = useState<Date | undefined>();
  const [error, setError] = useState<string>("");

  const dispatch = useAppDispatch();

  if (!isOpen) return null;

  const handleOnClose = () => {
    setMemberId(undefined);
    setConversationId("");
    setEndReason(undefined);
    setAdminAction(undefined);
    setCreatedAt(undefined);
    setError("");
    onClose?.();
  };

  const handleFilterChat = async () => {
    try {
      dispatch(
        fetchChatHistory({
          queryParams: {
            ...(memberId && { member_id: memberId }),
            ...(conversationId && { conversation_id: conversationId }),
            ...(endReason !== undefined && { end_reason: endReason }),
            ...(adminAction !== undefined && { admin_action: adminAction }),
            ...(createdAt && { created: createdAt.toISOString().slice(0, 10) }),
          },
        })
      );
    } catch (e) {
      setError(`Failed to filter chat. Please try again. ${e}`);
      return;
    }
    handleOnClose();
  };

  const chatFilterModalBody = (
    <div className={styles.form}>
      {/* Member ID */}
      <div className={styles.field}>
        <label htmlFor="member-id">
          <strong>Member Id</strong>
        </label>
        <input
          id="member-id"
          type="text"
          className={styles.inputField}
          value={memberId ?? ""}
          onChange={(e) => setMemberId(e.target.value || undefined)}
          placeholder="e.g. 303"
        />
      </div>

      {/* Conversation ID */}
      <div className={styles.field}>
        <label htmlFor="conversation-id">
          <strong>Conversation Id</strong>
        </label>
        <input
          id="conversation-id"
          type="text"
          className={styles.inputField}
          value={conversationId ?? ""}
          onChange={(e) => setConversationId(e.target.value || undefined)}
          placeholder="UUID..."
        />
      </div>

      {/* End Reason */}
      <div className={styles.field}>
        <label htmlFor="end-reason">
          <strong>End Reason</strong>
        </label>
        <select
          id="end-reason"
          className={styles["category-select"]}
          value={endReason ?? ""} // keep controlled
          onChange={(e) => {
            const v = e.target.value;
            setEndReason(v === "" ? undefined : (Number(v) as EndReason));
          }}
        >
          <option value="">All End Reasons</option>
          {endReasonOptions.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.name}
            </option>
          ))}
        </select>
      </div>

      {/* Admin Action */}
      <div className={styles.field}>
        <label htmlFor="admin-action">
          <strong>Admin Action</strong>
        </label>
        <select
          id="admin-action"
          className={styles["category-select"]}
          value={adminAction ?? ""} // keep controlled
          onChange={(e) => {
            const v = e.target.value;
            setAdminAction(v === "" ? undefined : (Number(v) as AdminAction));
          }}
        >
          <option value="">All Admin Actions</option>
          {adminActionOptions.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.name}
            </option>
          ))}
        </select>
      </div>

      {/* Created (UTC day) */}
      <div className={styles.field}>
        <label htmlFor="created-at">
          <strong>Created (UTC Day)</strong>
        </label>
        <input
          id="created-at"
          type="date"
          className={styles.inputField}
          value={createdAt ? createdAt.toISOString().slice(0, 10) : ""}
          onChange={(e) => {
            // store as Date at 00:00:00Z; backend expects YYYY-MM-DD, you'll format when submitting
            const v = e.target.value;
            setCreatedAt(v ? new Date(`${v}T00:00:00Z`) : undefined);
          }}
        />
      </div>
    </div>
  );

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div
            style={{
              flex: 1,
              flexDirection: "row",
              display: "flex",
              gap: "16px",
            }}
          ></div>
          <button className={styles.closeButton} onClick={handleOnClose}>
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          {chatFilterModalBody}
          {error && <div className={styles.error}>{error}</div>}
        </div>
        <div className={styles.modalFooter}>
          <button
            className={clsx(styles.button, styles.warning)}
            onClick={handleOnClose}
          >
            Cancel
          </button>
          <button
            className={clsx(styles.button, styles.primary)}
            onClick={handleFilterChat}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatFilterModal;
